import React from 'react';
import './FamilyQuestions.css';

/**
 * "From home" — questions the family wrote between lessons, surfaced in the
 * Arrive group (pass 3 C1/C2) so whoever is teaching this week sees them.
 *
 * WHY THIS EXISTS (Anthony, 2026-08-12): "the students don't always remember.
 * Sometimes they come into their lesson and they can't remember what it is they
 * wanted to tell us or ask us to work on." A student practising at home hits a
 * question, writes it in the portal, and it waits here until the lesson.
 *
 * PLACED WITH CARRY-FORWARD, NOT WITH THE NOTES, and that is the whole
 * placement argument: both answer "what do I need to know before I start", both
 * arrive from outside this lesson, and both are read once at the top and then
 * ignored. Putting these in the note list would mix family-authored text into a
 * teacher's working list, which is exactly the collision the separate
 * collection exists to avoid.
 *
 * SELF-HIDES WHEN THERE ARE NONE. The notes column is already cramped — that is
 * a live complaint on its own card — so this must cost zero pixels on the
 * common day when a family asked nothing. It renders only when there is
 * something a teacher would want.
 *
 * PRESENTATIONAL since pass 3 C2: the page owns the data (useFamilyQuestions)
 * and this renders it inside the Arrive group, in whichever mount is painted.
 *
 * OPEN ONLY. A resolved question is history; mid-lesson it is noise. The family
 * still sees it on their Songs page, which is where history belongs.
 */
const FamilyQuestions = ({ questions = [], error = false, resolving = new Set(), onAnswered }) => {
	if (error) {
		return (
			<div className="ld-famq ld-famq--error" role="status">
				<i className="fa-sharp fa-solid fa-triangle-exclamation" aria-hidden="true" />
				<span>Couldn’t load questions from home. Refresh to try again.</span>
			</div>
		);
	}

	if (!questions.length) return null;

	return (
		<section className="ld-famq" aria-label="Questions from home">
			<header className="ld-famq__head">
				<i className="fa-sharp fa-solid fa-comment-question" aria-hidden="true" />
				<span className="ld-famq__title">From home</span>
				<span className="ld-famq__count">{questions.length}</span>
			</header>

			<ul className="ld-famq__list">
				{questions.map((q) => {
					const busy = resolving.has(q.id);
					return (
						<li key={q.id} className="ld-famq__item">
							<div className="ld-famq__body">
								{/* The song is the anchor a teacher navigates by. */}
								{q.songName && <span className="ld-famq__song">{q.songName}</span>}
								<span className="ld-famq__text">{q.text}</span>
								{q.askedByName && (
									<span className="ld-famq__who">— {q.askedByName}</span>
								)}
							</div>
							<button
								type="button"
								className="ld-famq__done"
								disabled={busy}
								onClick={() => onAnswered && onAnswered(q)}
								aria-label={`Mark answered: ${q.text}`}>
								<i
									className={`fa-sharp fa-solid ${busy ? 'fa-spinner fa-spin' : 'fa-check'}`}
									aria-hidden="true"
								/>
								{busy ? 'Saving' : 'Answered'}
							</button>
						</li>
					);
				})}
			</ul>
		</section>
	);
};

export default FamilyQuestions;
